<template>
  <div class="orderman-settings">
    <v-card class="glass-card elevation-0">
      <!-- Header -->
      <div class="glass-header">
        <div class="d-flex align-center">
          <v-icon size="32" class="header-icon mr-3">mdi-tablet</v-icon>
          <span class="section-title">Orderman Postavke</span>
        </div>
      </div>

      <!-- Location Selector -->
      <div class="location-selector-section pa-4">
        <v-select
          v-model="locationId"
          :items="locations"
          :loading="loadingLocations"
          item-text="name"
          item-value="id"
          label="Odaberite prodajno mjesto"
          prepend-inner-icon="mdi-store-marker"
          dense
          outlined
          hide-details
          class="location-selector"
        >
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center">
              <v-icon small class="mr-2" color="primary">mdi-store-marker</v-icon>
              <span class="font-weight-medium">{{ item.name }}</span>
            </div>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-icon>
              <v-icon small color="primary">mdi-store-marker</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-select>
      </div>

      <!-- Tabs -->
      <v-tabs
        v-model="activeTab"
        class="orderman-tabs"
        background-color="transparent"
        color="primary"
        slider-color="primary"
        show-arrows
      >
        <v-tab class="tab-item">
          <v-icon left small>mdi-account-group</v-icon>
          Korisnici
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-format-list-numbered</v-icon>
          Slijedovi
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-shape</v-icon>
          Kategorije
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-cog</v-icon>
          Postavke
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-printer-settings</v-icon>
          Format Ispisa
        </v-tab>
      </v-tabs>

      <!-- Tab Content -->
      <v-tabs-items v-model="activeTab" class="transparent">
        <v-tab-item>
          <users-tab
            v-if="locationId && companyId"
            :location-id="locationId"
            :company-id="companyId"
          />
        </v-tab-item>

        <v-tab-item>
          <courses-tab
            v-if="locationId"
            :location-id="locationId"
          />
        </v-tab-item>

        <v-tab-item>
          <categories-tab
            v-if="locationId && companyId"
            :location-id="locationId"
            :company-id="companyId"
          />
        </v-tab-item>

        <v-tab-item>
          <settings-tab
            v-if="locationId"
            :location-id="locationId"
          />
        </v-tab-item>

        <v-tab-item>
          <print-format-tab
            v-if="locationId"
            :location-id="locationId"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import state from '@/state'
import { df } from '@/plugins/firebase'
import UsersTab from './components/UsersTab'
import CoursesTab from './components/CoursesTab'
import CategoriesTab from './components/CategoriesTab'
import SettingsTab from './components/SettingsTab'
import PrintFormatTab from './components/PrintFormatTab'

export default {
  name: 'OrdermanSettings',

  components: {
    UsersTab,
    CoursesTab,
    CategoriesTab,
    SettingsTab,
    PrintFormatTab
  },

  data () {
    return {
      activeTab: 0,
      locationId: null,
      companyId: null,
      locations: [],
      loadingLocations: false
    }
  },

  created () {
    this.loadContext()
  },

  methods: {
    async loadContext () {
      const pointOfSale = state.getPointOfSale()
      const company = state.getCurrentCompany()

      if (company && company.id) {
        this.companyId = company.id

        // Load locations for this company
        await this.loadLocations()

        // Set default location from point of sale or first location
        if (pointOfSale && pointOfSale.id) {
          this.locationId = pointOfSale.id
        } else if (this.locations.length > 0) {
          this.locationId = this.locations[0].id
        }
      }
    },

    async loadLocations () {
      if (!this.companyId) {
        return
      }

      this.loadingLocations = true

      try {
        const snapshot = await df
          .collection('locations')
          .where('company_id', '==', this.companyId)
          .get()

        this.locations = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Prodajno mjesto'
        }))
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri učitavanju prodajnih mjesta',
          color: 'error'
        })
      } finally {
        this.loadingLocations = false
      }
    }
  }
}
</script>

<style scoped>
.orderman-settings {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
}

.glass-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 32px 16px 24px;
  position: relative;
}

.section-title {
  font-size: 24px !important;
  font-weight: 700 !important;
  color: white !important;
  letter-spacing: 0.5px !important;
}

.header-icon {
  color: white !important;
}

.orderman-tabs {
  border-bottom: 1px solid rgba(0, 179, 190, 0.1);
}

.tab-item {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
}

.transparent {
  background-color: transparent !important;
}

.location-selector-section {
  border-bottom: 1px solid rgba(0, 179, 190, 0.1);
  background: rgba(255, 255, 255, 0.5) !important;
}

.location-selector {
  max-width: 500px;
}

.location-selector >>> .v-input__control {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px !important;
}

.location-selector >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-width: 1px !important;
}

.location-selector >>> .v-select__selection {
  font-size: 14px !important;
}

@media (max-width: 600px) {
  .orderman-settings {
    padding: 12px;
  }

  .glass-header {
    padding: 24px 16px 16px;
  }

  .section-title {
    font-size: 20px !important;
  }

  .location-selector {
    max-width: 100%;
  }
}
</style>
